import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Layers, Eye, PenLine } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Page } from "@/lib/types";
import { LeftPanel } from "@/components/builder/LeftPanel";
import { ContentTab } from "@/components/builder/ContentTab";
import { ProposalPage } from "@/components/proposal/ProposalPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const SAMPLE_QUOTE = {
  homeowner: { name: "Sample Homeowner", address: "123 Example Lane" }, current_version: 1, line_items: [], valid_until: null,
  pricing: { subtotal: 0, addons_total: 0, discounts: [], discount_total: 0, tax_rate: 0, tax: 0, total: 0 },
  cover_image_url: null, before_image_url: null, mockup_url: null,
};

export default function TemplateEditor() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tpl, setTpl] = useState<any | null>(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState("");
  const [sections, setSections] = useState<any[]>([]);
  const [pane, setPane] = useState<"pages" | "preview" | "edit">("preview");

  useEffect(() => {
    api.get("/templates").then((r) => { const t = r.data.find((x: any) => x.id === id); if (!t) { toast.error("Template not found"); nav("/settings/content"); return; } setTpl(t); setSelected(t.pages?.[0]?.id || ""); });
    api.get("/content-sections").then((r) => setSections(r.data));
  }, [id]);

  const update = (patch: any) => { setTpl((t: any) => ({ ...t, ...patch })); setDirty(true); };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put(`/templates/${id}`, { name: tpl.name, description: tpl.description, pages: tpl.pages, is_company: tpl.is_company ?? true }); setTpl(data); setDirty(false); toast.success("Template saved — applies to new quotes only"); }
    catch (e) { toast.error(errMsg(e)); } finally { setSaving(false); }
  };

  const page = useMemo(() => tpl?.pages.find((p: Page) => p.id === selected), [tpl, selected]);
  if (!tpl) return <div className="flex h-screen items-center justify-center text-sm text-gray-500">Loading template…</div>;

  return (
    <div className="flex h-screen flex-col overflow-hidden bg-gray-100" data-testid="template-editor">
      <div className="flex items-center gap-3 border-b border-gray-200 bg-white px-4 py-2.5">
        <button data-testid="template-back-btn" onClick={() => nav("/settings/content")} className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 hover:text-gray-900"><ArrowLeft size={18} /></button>
        <div className="flex-1">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">Template editor <span className="rounded-md bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">Edits affect future quotes only</span>{dirty && <span className="text-xs text-amber-600" data-testid="template-unsaved">• Unsaved</span>}</div>
          <div className="text-xs text-gray-500">{tpl.pages.length} pages · existing quotes and past versions are never changed</div>
        </div>
        <Button size="sm" data-testid="template-save-btn" onClick={save} disabled={saving || !dirty} className="bg-blue-700 hover:bg-blue-800"><Save size={14} className="mr-1.5" />{saving ? "Saving…" : "Save template"}</Button>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-1 lg:grid-cols-[260px_1fr_380px]">
        <div className={`${pane === "pages" ? "block" : "hidden"} min-h-0 lg:block`}>
        <LeftPanel quote={{ pages: tpl.pages, attachments: [] }} selected={selected} onSelect={(id) => { setSelected(id); setPane("preview"); }} editable sections={sections} showAttachments={false} onPagesChange={(pages) => update({ pages })} />
        </div>
        <main className={`${pane === "preview" ? "block" : "hidden"} scrollbar-thin overflow-y-auto p-3 pb-20 sm:p-6 lg:block lg:p-8 lg:pb-8`} data-testid="template-canvas">
          <div className="mx-auto max-w-[760px]">
            {page ? <div key={page.id} className="fade-up"><ProposalPage page={page} quote={SAMPLE_QUOTE} variant="paper" /></div> : <div className="text-center text-sm text-gray-500">Select a page</div>}
            <div className="mt-4 text-center text-xs text-gray-400">Preview uses sample homeowner data and branded placeholders</div>
          </div>
        </main>
        <aside className={`${pane === "edit" ? "block" : "hidden"} scrollbar-thin overflow-y-auto border-l border-gray-200 bg-white p-4 pb-20 lg:block lg:pb-4`} data-testid="template-right-panel">
          <div className="mb-5 space-y-3 border-b border-gray-200 pb-5">
            <div><Label className="text-xs">Template name</Label><Input data-testid="template-name-input" className="mt-1" value={tpl.name} onChange={(e) => update({ name: e.target.value })} /></div>
            <div><Label className="text-xs">Description</Label><Input data-testid="template-description-input" className="mt-1" value={tpl.description || ""} onChange={(e) => update({ description: e.target.value })} /></div>
          </div>
          {page ? <ContentTab page={page} editable sections={sections}
            onChange={(p) => update({ pages: tpl.pages.map((x: Page) => x.id === p.id ? p : x) })}
            onApplyStyleToAll={(style) => update({ pages: tpl.pages.map((x: Page) => ({ ...x, style: { ...style } })) })}
            onDelete={() => { const pages = tpl.pages.filter((x: Page) => x.id !== page.id); update({ pages }); setSelected(pages[0]?.id || ""); }} />
            : <p className="text-xs text-gray-500">Select a page to edit.</p>}
        </aside>
      </div>
      <nav className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-3 border-t border-gray-200 bg-white lg:hidden" data-testid="template-mobile-panes">
        {([["pages", "Pages", Layers], ["preview", "Preview", Eye], ["edit", "Edit", PenLine]] as const).map(([k, l, Icon]) => <button key={k} type="button" data-testid={`template-pane-${k}`} onClick={() => setPane(k)} className={`flex flex-col items-center gap-0.5 py-2 text-[11px] font-medium transition-colors ${pane === k ? "text-blue-700" : "text-gray-500"}`}><Icon size={17} />{l}</button>)}
      </nav>
    </div>
  );
}
